import { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiContext } from '@shared/contexts';
import { TQueryHookOpts } from '@shared/types';
import {
  EFolderType,
  isTFolder,
  TCountryRegistration,
  IFolder,
  TFolderReturn,
  TRootItems,
  TSparseItem,
} from '../types/folders.types';
import { TItem, TVersionPreference } from '@modules/items';
import { TERMINOLOGY } from '@shared/i18n';
import { sortItems, sortSparseItems } from '../helpers/sortItems.helper';
import { isProductFolder } from '../helpers/items.helper';
import { addItemsToFolderQueryData } from './itemQueryData';

export const useProductFolders = (opts?: TQueryHookOpts): TItem[] => {
  const { data: rootData } = useFolder(undefined, opts);
  return useMemo(() => {
    if (!rootData || isTFolder(rootData)) return [];
    return rootData.children.filter(isProductFolder);
  }, [rootData]);
};

// --- GET:
export const useFolder = (id?: string, opts?: TQueryHookOpts) => {
  // empty string for root folder (products)
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const [isProcessing, setIsProcessing] = useState(false);

  const getFolder = async (): Promise<IFolder | TRootItems> => {
    const { data } = await apiClient.get<TFolderReturn | TRootItems>(id ? `/folders/${id}` : '/folders');
    if (isTFolder(data)) {
      // Extract special folders
      const blocksFolder = data.rootChildren.find((item) => item.folderType === EFolderType.BLOCK);
      const snapshotsFolder = data.rootChildren.find((item) => item.folderType === EFolderType.SNAPSHOT);
      const jurisdictions = data.rootChildren.filter((item) => item.folderType === EFolderType.JURISDICTION);

      // Merge backend-resolved names (e.g. product names) with root-level child names
      const resolvedNames = data.rootChildren.reduce(
        (acc, item) => {
          acc[item._id] = item.name;
          return acc;
        },
        { ...data.resolvedNames } as Record<string, string>
      );

      // Final folder object for frontend consumption
      const folderData: IFolder = {
        ...data,
        jurisdictions,
        blocksFolder,
        snapshotsFolder,
        resolvedNames,
      };

      return folderData;
    }
    return data;
  };

  const { data, isError, error, isLoading } = useQuery({
    retry,
    refetchInterval: isProcessing ? 1500 : undefined,
    queryKey: [id ? `folder-${id}` : 'products'],
    queryFn: getFolder,
    enabled: opts?.enabled !== false,
  });
  useHandleQueryError(isError, error, `Failed to load folder ${id}!`, `Loading ${id ? 'folder' : 'products'} failed!`);

  useEffect(() => {
    if (data && isTFolder(data) && data.isProcessing !== isProcessing) {
      setIsProcessing(data.isProcessing);
    }
  }, [data]);

  return { error, data, isLoading };
};

export const useMarkFolderViewed = () => {
  const { apiClient, handleApiError } = useContext(ApiContext);

  const mutationFn = async (folderId: string): Promise<void> => {
    await apiClient.put(`/folders/${folderId}/viewed`);
  };

  return useMutation({
    mutationFn,
    onError: handleApiError(`Saving the recently opened ${TERMINOLOGY.folder.lower} failed!`, true),
  });
};

export const useGenerateIndex = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { folderId: string; mode: TVersionPreference }): Promise<TItem> => {
    const res = await apiClient.post(`/folders/${variables.folderId}/generate-index`, {
      mode: variables.mode,
    });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (item: TItem, variables) => {
      show('success', 'Index document created.');

      addItemsToFolderQueryData(queryClient, variables.folderId, [item]);
    },
    onError: handleApiError('Generating index failed!'),
  });
};

export const useCreate = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const getCreatedItemLabel = (item: TItem) => {
    if (item.folderType === EFolderType.PRODUCT) return TERMINOLOGY.product.upper;
    if (item.folderType === EFolderType.JURISDICTION) return TERMINOLOGY.jurisdiction.upper;
    if (item.folderType === EFolderType.COUNTRY && item.path.length === 1) return TERMINOLOGY.registrationCountry.upper;
    if (item.folderType === EFolderType.COUNTRY && item.path.length === 2)
      return `${TERMINOLOGY.registrationCountry.upper} ${TERMINOLOGY.registrationSingle.lower}`;
    return TERMINOLOGY.folder.upper;
  };

  const mutationFn = async (variables: {
    parentId?: string; // empty for product
    name: string;
    countryRegistration?: TCountryRegistration; // only for `ECountriesFolderType.COUNTRY`
    sourceProductId?: string; // only for a product
    resolvedProductNames?: Record<string, string>; // product ID → name, for cache update
  }): Promise<TItem> => {
    const res = await apiClient.post(`/folders`, {
      parent: variables.parentId,
      name: variables.name,
      countryRegistration: variables.countryRegistration,
      sourceProduct: variables.sourceProductId,
    });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (item: TItem, variables) => {
      show('success', `${getCreatedItemLabel(item)} created!`);

      queryClient.setQueryData(
        [variables.parentId ? `folder-${variables.parentId}` : 'products'],
        (oldData: TRootItems | IFolder | undefined): IFolder | TRootItems | undefined => {
          if (oldData) {
            let jurisdictions: TSparseItem[] = [];
            if (variables.parentId) {
              jurisdictions = (oldData as IFolder).jurisdictions;
              if (item.folderType === EFolderType.JURISDICTION)
                jurisdictions = sortSparseItems([...jurisdictions, { _id: item._id, name: item.name }]);
            }
            const resolvedNames = variables.resolvedProductNames
              ? { ...(oldData as IFolder).resolvedNames, ...variables.resolvedProductNames }
              : (oldData as IFolder).resolvedNames;
            return {
              ...oldData,
              children: sortItems([item, ...oldData.children]),
              jurisdictions,
              resolvedNames,
            };
          }
          return oldData;
        }
      );
    },
    onError: handleApiError('Creation failed!'),
  });
};
